import { useEffect, useState } from 'react';

import CheckboxField from '../../components/CheckboxField/CheckboxField';
import Description from '../../components/Description/Description';
import Group from '../../components/Group/Group';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const CoApplicantExpensePage = () => {
    const translate = useTranslation();
    const { coApplicantTotalSections, coApplicantTotalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    useComponentTracker('CoApplicantExpensePage');
    const defaultPagesAction = useDefaultPagesAction();
    const dispatch = useAppDispatch();

    useSetBannerType('bannerOne');

    const coApplicantMortgageToggle = useAppSelector(selectFieldValue)('coapplicantMortgageToggle') === 'true';
    const coApplicantCarLoanToggle = useAppSelector(selectFieldValue)('coapplicantCarLoanToggle') === 'true';
    const coApplicantCarLeasingMonthlyCostToggle =
        useAppSelector(selectFieldValue)('coapplicantCarLeasingMonthlyCostToggle') === 'true';
    const coApplicantStudentLoanToggle = useAppSelector(selectFieldValue)('coapplicantStudentLoanToggle') === 'true';

    useFieldToggle(coApplicantMortgageToggle, ['coapplicantMortgageDebt', 'coapplicantMortgageMonthlyCost']);
    useFieldToggle(coApplicantCarLoanToggle, ['coapplicantCarLoanSum', 'coapplicantCarLoanMonthlyCost']);
    useFieldToggle(coApplicantCarLeasingMonthlyCostToggle, ['coapplicantCarLeasingMonthlyCost']);
    useFieldToggle(coApplicantStudentLoanToggle, ['coapplicantStudentLoanSum', 'coapplicantStudentLoanMonthlyCost']);

    const [requiredFields, setRequiredFields] = useState([]);
    const validationErrors = useValidation(requiredFields);

    useEffect(() => {
        const result: any = [];

        if (coApplicantMortgageToggle) {
            result.push('coapplicantMortgageDebt');
            result.push('coapplicantMortgageMonthlyCost');
        }

        if (coApplicantCarLoanToggle) {
            result.push('coapplicantCarLoanSum');
            result.push('coapplicantCarLoanMonthlyCost');
        }

        if (coApplicantCarLeasingMonthlyCostToggle) {
            result.push('coapplicantCarLeasingMonthlyCost');
        }

        if (coApplicantStudentLoanToggle) {
            result.push('coapplicantStudentLoanSum');
            result.push('coapplicantStudentLoanMonthlyCost');
        }

        setRequiredFields(result);
    }, [
        coApplicantMortgageToggle,
        coApplicantCarLoanToggle,
        coApplicantCarLeasingMonthlyCostToggle,
        coApplicantStudentLoanToggle,
    ]);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next') {
            if (validationErrors.length > 0) {
                scrollToFieldWithError(validationErrors);
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });
                return false;
            }
        }

        return defaultPagesAction(action);
    };

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = (value ?? 0) > '0';

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : translate('field.applicant.min-value.error'),
        };
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.expense.title')} />
            <Description text={translate('page.expense.description')} />
            <ShowDetailsModal
                label={translate('dialog.know-more.label')}
                dialogTitle={translate('dialog.know-more.label')}
                dialogContent={translate('dialog.expense.content')}
            />

            <CheckboxField
                name={'coapplicantMortgageToggle'}
                label={translate('field.applicant-mortage-toggle.label')}
            />
            {coApplicantMortgageToggle && (
                <Group>
                    <MonetaryField
                        name="coapplicantMortgageDebt"
                        label={translate('field.applicant-debt.label')}
                        customValidation={minValueValidation}
                    />
                    <MonetaryField
                        name="coapplicantMortgageMonthlyCost"
                        label={translate('field.applicant-debt-monthly-cost.label')}
                        customValidation={minValueValidation}
                    />
                </Group>
            )}

            <CheckboxField
                name={'coapplicantCarLoanToggle'}
                label={translate('field.applicant-car-loan-toggle.label')}
            />
            {coApplicantCarLoanToggle && (
                <Group>
                    <MonetaryField
                        name="coapplicantCarLoanSum"
                        label={translate('field.applicant-debt.label')}
                        customValidation={minValueValidation}
                    />
                    <MonetaryField
                        name="coapplicantCarLoanMonthlyCost"
                        label={translate('field.applicant-debt-monthly-cost.label')}
                        customValidation={minValueValidation}
                    />
                </Group>
            )}

            <CheckboxField
                name={'coapplicantCarLeasingMonthlyCostToggle'}
                label={translate('field.applicant-car-leasing-monthly-cost-toggle.label')}
            />
            {coApplicantCarLeasingMonthlyCostToggle && (
                <MonetaryField
                    name="coapplicantCarLeasingMonthlyCost"
                    label={translate('field.applicant-car-leasing-monthly-cost.label')}
                    customValidation={minValueValidation}
                />
            )}

            <CheckboxField
                name={'coapplicantStudentLoanToggle'}
                label={translate('field.applicant-student-loan-toggle.label')}
            />
            {coApplicantStudentLoanToggle && (
                <Group>
                    <MonetaryField
                        name="coapplicantStudentLoanSum"
                        label={translate('field.applicant-debt.label')}
                        customValidation={minValueValidation}
                    />
                    <MonetaryField
                        name="coapplicantStudentLoanMonthlyCost"
                        label={translate('field.applicant-debt-monthly-cost.label')}
                        customValidation={minValueValidation}
                    />
                </Group>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={coApplicantTotalSections}
                numberOfPagesInSection={coApplicantTotalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default CoApplicantExpensePage;
